/**
 * OppmoteMalEditor - Rediger oppmøtetider for produksjonskategori-mal
 * MVP versjon (uten talent-kobling)
 */

import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Clock, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { produksjonAPI } from '@/services/api/produksjon.api';
import { TalentSelector, SelectedTalent, fjernDekkedeValg } from '@/components/common/TalentSelector';
import { cn } from '@/lib/utils';

interface OppmoteMalTalent {
  id: number;
  oppmote_mal_id: number;
  talent_id?: number | null;
  talent_kategori_id?: number | null;
  talent_navn?: string | null;
  talent_kategori_sti?: string | null;
  kategori_navn?: string | null;
  kategori_sti?: string | null;
}

interface OppmoteMal {
  id: number;
  kategori_id: number;
  navn: string;
  beskrivelse?: string | null;
  minutter_før_start: number;
  rekkefølge: number;
  talenter?: OppmoteMalTalent[]; // Talent-koblinger (hvis tilgjengelig)
}

interface Props {
  kategoriId: number;
  onSave: () => void;
}

interface OppmoteFormData {
  id?: number;
  navn: string;
  beskrivelse: string;
  minutterFørStart: number; // Minutter før produksjonsstart
  rekkefølge: number;
  selectedTalents: SelectedTalent[]; // Valgte talenter/kategorier
}

const OppmoteMalEditor: React.FC<Props> = ({ kategoriId, onSave }) => {
  const [oppmoteMal, setOppmoteMal] = useState<OppmoteMal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<OppmoteFormData>({
    navn: '',
    beskrivelse: '',
    minutterFørStart: 30,
    rekkefølge: 0,
    selectedTalents: [],
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loadingTalents, setLoadingTalents] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetchOppmoteMal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategoriId]);

  const fetchOppmoteMal = async () => {
    try {
      setLoading(true);
      setError(null);
      const { oppmoteMal: data } = await produksjonAPI.getOppmoteMal(kategoriId);
      setOppmoteMal(data);
    } catch (err) {
      console.error('Feil ved henting av oppmøte-mal:', err);
      setError('Kunne ikke hente oppmøte-mal');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = async (item?: OppmoteMal) => {
    if (item) {
      // Hent talent-koblinger for denne oppmøte-malen
      let selectedTalents: SelectedTalent[] = [];
      try {
        // Backend returnerer talents i oppmote-mal response, men vi kan også hente separat
        if (item.talenter && item.talenter.length > 0) {
          selectedTalents = item.talenter.map(t => ({
            id: t.talent_id || t.talent_kategori_id || 0,
            navn: t.talent_navn || t.kategori_navn || '',
            erKategori: !!t.talent_kategori_id,
            kategoriSti: t.talent_kategori_sti || t.kategori_sti || undefined,
          }));
        }
      } catch (err) {
        console.error('Feil ved henting av talent-koblinger:', err);
      }
      
      setFormData({
        id: item.id,
        navn: item.navn,
        beskrivelse: item.beskrivelse || '',
        minutterFørStart: item.minutter_før_start,
        rekkefølge: item.rekkefølge,
        selectedTalents,
      });
      setEditingId(item.id);
    } else {
      setFormData({
        navn: '',
        beskrivelse: '',
        minutterFørStart: 30,
        rekkefølge: oppmoteMal.length,
        selectedTalents: [],
      });
      setEditingId(null);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.navn.trim()) {
      setError('Navn er påkrevd');
      return;
    }

    if (formData.minutterFørStart < 0) {
      setError('Minutter før start må være >= 0');
      return;
    }

    try {
      let oppmoteId: number;
      
      if (editingId) {
        const result = await produksjonAPI.updateOppmoteInMal(kategoriId, editingId, {
          navn: formData.navn.trim(),
          beskrivelse: formData.beskrivelse.trim() || undefined,
          minutterFørStart: formData.minutterFørStart,
          rekkefølge: formData.rekkefølge,
        });
        oppmoteId = editingId;
      } else {
        const result = await produksjonAPI.addOppmoteToMal(kategoriId, {
          navn: formData.navn.trim(),
          beskrivelse: formData.beskrivelse.trim() || undefined,
          minutterFørStart: formData.minutterFørStart,
          rekkefølge: formData.rekkefølge,
        });
        oppmoteId = result.oppmote.id;
      }

      // Håndter talent-koblinger (valgt kategori overstyrer talenter/underkategorier i den)
      const valgteTalenter = fjernDekkedeValg(formData.selectedTalents);
      if (valgteTalenter.length > 0) {
        // Hent eksisterende koblinger først (for å kunne fjerne de som ikke lenger er valgt)
        let existingTalents: OppmoteMalTalent[] = [];
        try {
          const existing = oppmoteMal.find(o => o.id === oppmoteId);
          existingTalents = existing?.talenter || [];
        } catch (err) {
          // Ignorer hvis ikke tilgjengelig
        }

        // Finn koblinger som skal fjernes
        const toRemove = existingTalents.filter(et => {
          const stillSelected = valgteTalenter.some(st => {
            if (et.talent_id && !st.erKategori) {
              return st.id === et.talent_id;
            }
            if (et.talent_kategori_id && st.erKategori) {
              return st.id === et.talent_kategori_id;
            }
            return false;
          });
          return !stillSelected;
        });

        // Fjern koblinger som ikke lenger er valgt
        for (const removeTalent of toRemove) {
          try {
            await produksjonAPI.removeTalentFromOppmoteMal(kategoriId, oppmoteId, {
              talentId: removeTalent.talent_id || undefined,
              talentKategoriId: removeTalent.talent_kategori_id || undefined,
            });
          } catch (err) {
            console.error('Feil ved fjerning av talent-kobling:', err);
          }
        }

        // Legg til nye koblinger
        for (const selectedTalent of valgteTalenter) {
          const alreadyExists = existingTalents.some(et => {
            if (selectedTalent.erKategori) {
              return et.talent_kategori_id === selectedTalent.id;
            } else {
              return et.talent_id === selectedTalent.id;
            }
          });

          if (!alreadyExists) {
            try {
              await produksjonAPI.addTalentToOppmoteMal(kategoriId, oppmoteId, {
                talentId: selectedTalent.erKategori ? undefined : selectedTalent.id,
                talentKategoriId: selectedTalent.erKategori ? selectedTalent.id : undefined,
              });
            } catch (err) {
              console.error('Feil ved legg til talent-kobling:', err);
            }
          }
        }
      } else {
        // Fjern alle koblinger hvis ingen er valgt
        try {
          const existing = oppmoteMal.find(o => o.id === oppmoteId);
          if (existing?.talenter && existing.talenter.length > 0) {
            for (const talent of existing.talenter) {
              await produksjonAPI.removeTalentFromOppmoteMal(kategoriId, oppmoteId, {
                talentId: talent.talent_id || undefined,
                talentKategoriId: talent.talent_kategori_id || undefined,
              });
            }
          }
        } catch (err) {
          console.error('Feil ved fjerning av alle talent-koblinger:', err);
        }
      }

      await fetchOppmoteMal();
      closeDialog();
      onSave();
    } catch (err: any) {
      console.error('Feil ved lagring av oppmøte-mal:', err);
      setError(err.response?.data?.error || 'Kunne ikke lagre oppmøte-mal');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Er du sikker på at du vil slette denne oppmøtetiden?')) {
      return;
    }

    try {
      await produksjonAPI.removeOppmoteFromMal(kategoriId, id);
      await fetchOppmoteMal();
      onSave();
    } catch (err) {
      console.error('Feil ved sletting av oppmøte-mal:', err);
      toast.error('Kunne ikke slette oppmøte-mal');
    }
  };

  const formatMinuterFør = (minutter: number): string => {
    if (minutter === 0) {
      return 'Ved start';
    }
    const hours = Math.floor(minutter / 60);
    const minutes = minutter % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')} før start`;
    }
    return `${minutes} min før start`;
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Laster oppmøte-mal...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Oppmøtetider</h3>
          <p className="text-sm text-muted-foreground">
            Definer standard oppmøtetider for denne produksjonsmalen. Disse kopieres til nye produksjoner.
          </p>
        </div>
        <Button onClick={() => openDialog()} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Legg til oppmøtetid
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {error}
        </div>
      )}

      {oppmoteMal.length === 0 ? (
        <div className="text-center py-12 bg-muted rounded-md">
          <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">Ingen oppmøtetider definert</p>
          <Button onClick={() => openDialog()} variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Legg til første oppmøtetid
          </Button>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="w-[5%]"></TableHead>
                <TableHead className="w-[30%]">Navn</TableHead>
                <TableHead className="w-[25%]">Tid før start</TableHead>
                <TableHead className="w-[20%]">Beskrivelse</TableHead>
                <TableHead className="w-[20%]">Talenter</TableHead>
                <TableHead className="w-[10%] text-right">Handlinger</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {oppmoteMal.map((item, index) => (
                <TableRow key={item.id} className={cn(
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                )}>
                  <TableCell>
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{item.navn}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{formatMinuterFør(item.minutter_før_start)}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {item.beskrivelse || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {item.talenter && item.talenter.length > 0 ? (
                        item.talenter.map((talent, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {talent.talent_navn || talent.kategori_navn}
                            {talent.talent_kategori_id && ' (gruppe)'}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">Ingen</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => openDialog(item)}
                        variant="ghost"
                        size="sm"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(item.id)}
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog for å legge til/redigere oppmøtetid */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Rediger oppmøtetid' : 'Legg til oppmøtetid'}
            </DialogTitle>
            <DialogDescription>
              Angi navn og tid før produksjonsstart
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="navn">Navn *</Label>
              <Input
                id="navn"
                value={formData.navn}
                onChange={(e) => setFormData({ ...formData, navn: e.target.value })}
                placeholder="F.eks. Crew oppmøte, Artist oppmøte"
                required
              />
            </div>

            <div>
              <Label htmlFor="beskrivelse">Beskrivelse</Label>
              <Textarea
                id="beskrivelse"
                value={formData.beskrivelse}
                onChange={(e) => setFormData({ ...formData, beskrivelse: e.target.value })}
                placeholder="Valgfri beskrivelse..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="minutterFørStart">Minutter før produksjonsstart *</Label>
              <Input
                id="minutterFørStart"
                type="number"
                min="0"
                value={formData.minutterFørStart}
                onChange={(e) =>
                  setFormData({ ...formData, minutterFørStart: parseInt(e.target.value) || 0 })
                }
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                Eksempel: 30 = oppmøte 30 minutter før produksjonsstart
                <br />
                Eksempel: 90 = oppmøte 1 time og 30 minutter før start
              </p>
            </div>

            <div>
              <Label>For hvem gjelder oppmøtetiden?</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Velg talenter eller talentgrupper som skal ha denne oppmøtetiden
              </p>
              <TalentSelector
                selectedTalents={formData.selectedTalents}
                onTalentsChange={(talents) =>
                  setFormData({ ...formData, selectedTalents: talents })
                }
                allowGroups={true}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <Clock className="h-4 w-4" />
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Avbryt
              </Button>
              <Button type="submit">
                {editingId ? 'Lagre endringer' : 'Legg til'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OppmoteMalEditor;

